import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from './ui/chart';

function DashboardHome() {
  const [analytics, setAnalytics] = useState({ fds: 0, info: 0, wayleave: 0 });
  const [chartData, setChartData] = useState({ trends: [], damageTypes: [], topStations: [] });
  const [wayleaveChartData, setWayleaveChartData] = useState({ statusData: [], ownerData: [], activityData: [] });
  const [mCollectData, setMCollectData] = useState({ totalCount: 0, monthlyData: [] });
  const [stationOverview, setStationOverview] = useState({ ownerData: [], stationTypeData: [], stationTypeOwners: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [wayleaveChartsLoading, setWayleaveChartsLoading] = useState(true);
  const [mCollectLoading, setMCollectLoading] = useState(true);
  const [stationOverviewLoading, setStationOverviewLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
    fetchChartData();
    fetchWayleaveChartData();
    fetchMCollectData();
    fetchStationOverview();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/analytics', { withCredentials: true });
      setAnalytics(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Analytics error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      setChartsLoading(true);
      const response = await axios.get('/api/analytics/fds-charts', { withCredentials: true });
      setChartData(response.data);
    } catch (err) {
      console.error('Chart data error:', err);
    } finally {
      setChartsLoading(false);
    }
  };

  const fetchWayleaveChartData = async () => {
    try {
      setWayleaveChartsLoading(true);
      const response = await axios.get('/api/analytics/wayleave-charts', { withCredentials: true });
      setWayleaveChartData(response.data);
    } catch (err) {
      console.error('Wayleave chart data error:', err);
    } finally {
      setWayleaveChartsLoading(false);
    }
  };

  const fetchMCollectData = async () => {
    try {
      setMCollectLoading(true);
      const response = await axios.get('/api/analytics/mcollect', { withCredentials: true });
      setMCollectData(response.data);
    } catch (err) {
      console.error('mCollect data error:', err);
    } finally {
      setMCollectLoading(false);
    }
  };

  const fetchStationOverview = async () => {
    try {
      setStationOverviewLoading(true);
      const response = await axios.get('/api/analytics/station-overview', { withCredentials: true });
      setStationOverview(response.data);
    } catch (err) {
      console.error('Station overview error:', err);
    } finally {
      setStationOverviewLoading(false);
    }
  };

  const OWNER_COLORS = { wcg: '#001489', sanral: '#007663', discontinued: '#d1d5db' };
  const DONUT_COLORS = ['#001489', '#007663', '#d1d5db', '#f97316'];
  const STATION_TYPE_LABELS = { P: 'Permanent', T: 'Temporary', S: 'Secondary', M: 'Merged', D: 'Discontinued', X: 'Sanral', Xz: 'Sanral Piezo', Pz: 'WCG Piezo' };

  // --- Chart configs ---

  // 1. Station Owners donut config (dynamic based on data)
  const ownerChartConfig = Object.fromEntries(
    (stationOverview.ownerData || []).map((entry, index) => [
      entry.name,
      {
        label: entry.name,
        color: OWNER_COLORS[entry.name?.toLowerCase()] || DONUT_COLORS[index % DONUT_COLORS.length],
      },
    ])
  );

  // 2. Station Type bar config
  const stationTypeChartConfig = {
    value: { label: 'Stations', color: '#60a5fa' },
  };

  // 3. Damage Trends line chart
  const damageTrendsConfig = {
    count: { label: 'Damage Incidents', color: '#f97316' },
  };

  // 4. Damage Types bar chart
  const damageTypesConfig = {
    count: { label: 'Count', color: '#10b981' },
  };

  // 5. Wayleaves by Progress Status
  const wayleaveStatusConfig = {
    count: { label: 'Count', color: '#8b5cf6' },
  };

  // 6. Wayleaves by Owner (dynamic)
  const wayleaveOwnerConfig = Object.fromEntries(
    (wayleaveChartData.ownerData || []).slice(0, 5).map((entry) => [
      entry.owner,
      {
        label: entry.owner,
        color: OWNER_COLORS[entry.owner?.toLowerCase()] || '#06b6d4',
      },
    ])
  );

  // 7. mCollect Field Visits
  const mCollectConfig = {
    count: { label: 'Field Visits', color: '#f97316' },
  };

  const navigate = useNavigate();

  const StatCard = ({ title, value, icon, color, link }) => (
    <div
      className="card hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => link && navigate(link)}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-3xl font-bold mt-2 ${color}`}>{value.toLocaleString()}</p>
        </div>
        <div className={`p-4 rounded-full ${color.replace('text', 'bg').replace('600', '100')}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Station Count"
          value={analytics.info}
          color="text-green-600"
          link="/station-info"
          icon={
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        <StatCard
          title="Frequently Damaged Station Count"
          value={analytics.fds}
          color="text-blue-600"
          link="/location"
          icon={
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />

        <StatCard
          title="Wayleave Count"
          value={analytics.wayleave}
          link="/wayleave"
          color="text-purple-600"
          icon={
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
        />
      </div>

      {/* Station Overview Charts */}
      {!stationOverviewLoading && ((stationOverview.ownerData?.length > 0) || (stationOverview.stationTypeData?.length > 0)) && (
        <div className="mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Station Overview</h2>
            <p className="text-sm text-gray-600">Station ownership and type distribution</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Chart 1: Station Owners Donut Chart */}
            <div className="card">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Station Owners</h2>
              <ChartContainer config={ownerChartConfig} className="aspect-auto h-[180px] w-full">
                <PieChart>
                  <Pie
                    data={stationOverview.ownerData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={65}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {(stationOverview.ownerData || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={OWNER_COLORS[entry.name?.toLowerCase()] || DONUT_COLORS[index % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                </PieChart>
              </ChartContainer>
            </div>

            {/* Chart 2: Station Type Bar Chart */}
            <div className="card">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Station Type</h2>
              <ChartContainer config={stationTypeChartConfig} className="aspect-auto h-[180px] w-full">
                <BarChart data={(stationOverview.stationTypeData || []).map(d => {
                  const { name, ...owners } = d;
                  return { name: STATION_TYPE_LABELS[name] || name, value: Object.values(owners).reduce((sum, v) => sum + (v || 0), 0) };
                }).sort((a, b) => b.value - a.value)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="value" fill="var(--color-value)" name="Stations" />
                </BarChart>
              </ChartContainer>
            </div>
          </div>
        </div>
      )}

      {stationOverviewLoading && (
        <div className="card mb-6">
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-600">Loading station overview...</div>
          </div>
        </div>
      )}

      {/* FDS Charts - 3 Column Layout */}
      {!chartsLoading && chartData.trends?.length > 0 && (
        <div className="mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Frequently Damaged Station Analytics</h2>
            <p className="text-sm text-gray-600">Overview of frequently damaged stations and damage type</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Chart 3: Damage Trends Area Chart */}
          <div className="card">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Damage Trends (Last 12 Months)</h2>
            <ChartContainer config={damageTrendsConfig} className="min-h-[200px] w-full">
              <AreaChart data={chartData.trends.slice(0, 12).reverse()}>
                <defs>
                  <linearGradient id="damageTrendsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-count)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-count)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{fontSize: 10}} />
                <YAxis tick={{fontSize: 10}} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Area type="monotone" dataKey="count" stroke="var(--color-count)" strokeWidth={2} fill="url(#damageTrendsFill)" name="Damage Incidents" />
              </AreaChart>
            </ChartContainer>
          </div>

          {/* FDS Summary Stats */}
          <div className="card">
            <h2 className="text-base font-semibold text-gray-900 mb-3">FDS Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Total Damage Types</span>
                <span className="text-sm font-semibold text-gray-900">{chartData.damageTypes?.length || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Most Common Type</span>
                <span className="text-sm font-semibold text-gray-900">
                  {chartData.damageTypes[0]?.damageType || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Stations Affected</span>
                <span className="text-sm font-semibold text-gray-900">{chartData.topStations?.length || 0}+</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Most Damaged Station</span>
                <span className="text-sm font-semibold text-blue-600">
                  {chartData.topStations[0]?.stationNo || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Chart 4: Damage Types Distribution Bar Chart */}
          <div className="card">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Damage Types Distribution</h2>
            <ChartContainer config={damageTypesConfig} className="min-h-[200px] w-full">
              <BarChart data={chartData.damageTypes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="damageType" tick={{fontSize: 9}} angle={-45} textAnchor="end" height={80} />
                <YAxis tick={{fontSize: 10}} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill="var(--color-count)" name="Count" />
              </BarChart>
            </ChartContainer>
          </div>
          </div>
        </div>
      )}

      {chartsLoading && (
        <div className="card mb-6">
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-600">Loading FDS charts...</div>
          </div>
        </div>
      )}

      {/* Wayleave Charts - 3 Column Layout */}
      {!wayleaveChartsLoading && wayleaveChartData.statusData?.length > 0 && (
        <div className="mb-6">
          <div className="mb-3">
            <h2 className="text-xl font-semibold text-gray-900">Wayleave Analytics</h2>
            <p className="text-sm text-gray-600">Overview of wayleave progress and distribution</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Chart 5: Wayleave by Progress Status Bar Chart */}
            <div className="card">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Wayleaves by Progress Status</h2>
              <ChartContainer config={wayleaveStatusConfig} className="min-h-[200px] w-full">
                <BarChart data={wayleaveChartData.statusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" tick={{fontSize: 9}} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{fontSize: 10}} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" fill="var(--color-count)" name="Count" />
                </BarChart>
              </ChartContainer>
            </div>

            {/* Wayleave Activity Summary */}
            <div className="card">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Wayleave Activity Status</h2>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Total Wayleaves</span>
                  <span className="text-sm font-semibold text-gray-900">{wayleaveChartData.totalWayleaves}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Active Wayleaves</span>
                  <span className="text-sm font-semibold text-green-600">
                    {wayleaveChartData.totalWayleaves - wayleaveChartData.noActivityCount}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">No Activity</span>
                  <span className="text-sm font-semibold text-red-600">{wayleaveChartData.noActivityCount}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600">Completion Rate</span>
                  <span className="text-sm font-semibold text-purple-600">
                    {wayleaveChartData.totalWayleaves > 0
                      ? Math.round(((wayleaveChartData.totalWayleaves - wayleaveChartData.noActivityCount) / wayleaveChartData.totalWayleaves) * 100)
                      : 0}%
                  </span>
                </div>
              </div>
            </div>

            {/* Chart 6: Wayleave by Owner Bar Chart */}
            <div className="card">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Wayleaves by Owner</h2>
              <ChartContainer config={wayleaveOwnerConfig} className="min-h-[200px] w-full">
                <BarChart data={wayleaveChartData.ownerData.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="owner" tick={{fontSize: 9}} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{fontSize: 10}} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" name="Count">
                    {(wayleaveChartData.ownerData || []).slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={OWNER_COLORS[entry.owner?.toLowerCase()] || '#06b6d4'} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </div>
          </div>
        </div>
      )}

      {wayleaveChartsLoading && (
        <div className="card mb-6">
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-600">Loading wayleave charts...</div>
          </div>
        </div>
      )}

      {/* mCollect Status */}
      <div className="card mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">mCollect Status</h2>
            <p className="text-sm text-gray-600">Field visit data from mCollect app</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="p-3 bg-orange-100 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Field Visits</p>
              <p className="text-2xl font-bold text-orange-600">
                {mCollectLoading ? '...' : mCollectData.totalCount.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {mCollectLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-gray-600">Loading mCollect data...</div>
          </div>
        ) : mCollectData.monthlyData?.length > 0 ? (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Field Visits Per Month</h3>
            {/* Chart 7: mCollect Field Visits Area Chart */}
            <ChartContainer config={mCollectConfig} className="aspect-auto h-[120px] w-full">
              <AreaChart data={mCollectData.monthlyData}>
                <defs>
                  <linearGradient id="mCollectFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-count)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-count)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 9 }}
                  angle={-45}
                  textAnchor="end"
                  height={40}
                />
                <YAxis tick={{ fontSize: 9 }} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="var(--color-count)"
                  strokeWidth={2}
                  fill="url(#mCollectFill)"
                  dot={{ fill: '#f97316', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#ea580c' }}
                  name="Field Visits"
                />
              </AreaChart>
            </ChartContainer>
            <div className="mt-3 flex justify-between text-xs text-gray-500">
              <span>Earliest: {mCollectData.monthlyData[0]?.month}</span>
              <span>Latest: {mCollectData.monthlyData?.[mCollectData.monthlyData.length - 1]?.month}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 text-gray-500">
            No monthly data available
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardHome;
